import { useCallback, useEffect, useMemo } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'

import { DEFAULT_ENTITIES_LIMIT } from 'shared/consts'
import { GET_FACT_SOURCES } from 'modulesCommon/graphql/queries'
import {
  countryCodeVar,
  factSourcesVar,
  feedFiltersVar,
  feedTotalVar
} from 'modulesCommon/store'

import { filterFactSources } from 'shared/utils/factSources'
import { GET_FEEDS } from 'modules/Feed/graphql/queries'
import { feedVar } from 'modules/Feed/store'
import { useZoneManager } from '../../../shared/hooks/useZoneManager'

const useFeed = () => {
  const { currentZone } = useZoneManager()
  const feed = useReactiveVar(feedVar)
  const feedTotal = useReactiveVar(feedTotalVar)
  const feedFilters = useReactiveVar(feedFiltersVar)
  const country = useReactiveVar(countryCodeVar)
  const factSources = useReactiveVar(factSourcesVar)

  const { loading: factSourcesLoading, error: factSourcesError } = useQuery(GET_FACT_SOURCES, {
    variables: { countryCode: '' },
    onCompleted: (data) => {
      factSourcesVar([...(data?.getFactSources || [])])
    }
  })

  const { label_id, ...restFeedFilters } = feedFilters
  const {
    data: feedFetched,
    loading: feedLoading,
    error: feedError,
    refetch,
    fetchMore
  } = useQuery(GET_FEEDS, {
    notifyOnNetworkStatusChange: true,
    skip: !country || !currentZone?.id,
    variables: {
      limit: DEFAULT_ENTITIES_LIMIT,
      offset: 0,
      filters: {
        country,
        ...restFeedFilters,
        // ...(label_id?.length && label_id !== 'all' && { label_id: label_id }),
        label_id: currentZone?.id.toString()
      }
    }
  })

  const increaseFeedOffset = useCallback(() => {
    fetchMore({
      variables: {
        offset: feed.length
      }
    })
  }, [feed.length, fetchMore])

  const factSourcesList = useMemo(() => filterFactSources(factSources, country), [factSources, country])


  useEffect(() => {
    if (feedFetched && !feedError) {
      feedVar(feedFetched.getFeeds.feeds)
      feedTotalVar(feedFetched.getFeeds.total)
    }
  }, [feedFetched, feed, feedError])

  useEffect(() => {
    if (currentZone?.id && feedFilters) {
      refetch()
    }
  }, [feedFilters, refetch, currentZone?.id])


  useLoading({ isLoading: feedLoading || factSourcesLoading })
  useServerError([feedError, factSourcesError])

  return {
    feed,
    country,
    factSourcesList,
    feedLoading,
    feedTotal,
    increaseFeedOffset
  }
}

export default useFeed
